import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  CssBaseline,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ArtTrack as ArtworksIcon,
  Archive as ArchiveIcon,
  TableChart as ListsIcon,
  People as AuthorsIcon,
  Assessment as ReportsIcon,
  AdminPanelSettings as AdminIcon,
  AccountBalance as FundsIcon,
  Group as UsersIcon,
  AccountCircle,
  Logout,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, isAdmin } from '../utils/permissions';
import {
  clearListStateOnSectionChange,
  getArtworksListPath,
  isArtworksSection,
} from '../utils/listViewState';

const drawerWidth = 240;

/** Обычные разделы — выше разделителя */
const mainMenuItems = [
  { text: 'Главная', icon: <DashboardIcon />, path: '/', show: (user) => user?.role !== 'visitor' },
  { text: 'Произведения', icon: <ArtworksIcon />, path: '/artworks', show: () => true },
  { text: 'Архив', icon: <ArchiveIcon />, path: '/archive', show: (user) => hasPermission(user, 'view_archive') },
  { text: 'Списки', icon: <ListsIcon />, path: '/lists', show: (user) => hasPermission(user, 'export_documents') },
  { text: 'Авторы', icon: <AuthorsIcon />, path: '/authors', show: (user) => hasPermission(user, 'view_authors') },
  { text: 'Отчёты', icon: <ReportsIcon />, path: '/reports', show: (user) => hasPermission(user, 'view_reports') },
  { text: 'Пользователи', icon: <UsersIcon />, path: '/users', show: (user) => user?.role === 'owner' },
];

/** Админ-панель и фонды — ниже разделителя */
const adminMenuItems = [
  { text: 'Админ-панель', icon: <AdminIcon />, path: '/admin', show: (user) => isAdmin(user) },
  { text: 'Фонды', icon: <FundsIcon />, path: '/admin/funds', show: (user) => hasPermission(user, 'manage_funds') },
];

function isMenuItemSelected(pathname, itemPath) {
  if (itemPath === '/' || itemPath === '/admin') {
    return pathname === itemPath;
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function renderMenuItem(item, selected, onClick) {
  return (
    <ListItem
      button
      key={item.text}
      selected={selected}
      onClick={onClick}
      sx={{
        '&.Mui-selected': {
          backgroundColor: '#9ACBBF',
          '&:hover': {
            backgroundColor: '#A7CBC2',
          },
        },
        '&:hover': {
          backgroundColor: '#99BFC7',
        },
      }}
    >
      <ListItemIcon
        sx={{
          color: selected ? '#226353' : '#21535E',
        }}
      >
        {item.icon}
      </ListItemIcon>
      <ListItemText
        primary={item.text}
        sx={{
          color: selected ? '#5A5A5A' : '#57726C',
        }}
      />
    </ListItem>
  );
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const visibleMainItems = mainMenuItems.filter((item) => item.show(user));
  const visibleAdminItems = adminMenuItems.filter((item) => item.show(user));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = (targetPath) => {
    clearListStateOnSectionChange(location.pathname, targetPath);
    // Внутри раздела произведений возвращаемся к списку с сохранённым поиском
    if (targetPath === '/artworks' && isArtworksSection(location.pathname)) {
      navigate(getArtworksListPath());
    } else {
      navigate(targetPath);
    }
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar
        sx={{
          backgroundColor: '#648991', // Вторичный цвет A для заголовка
          flexShrink: 0,
        }}
      >
        <Typography 
          variant="h6" 
          noWrap 
          component="div" 
          sx={{ 
            color: '#FFFFFF !important',
            fontWeight: 600
          }}
        >
          Collection
        </Typography>
      </Toolbar>
      <List
        sx={{
          backgroundColor: '#A7CBC2', // Светлый фон для меню
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {visibleMainItems.map((item) =>
          renderMenuItem(
            item,
            isMenuItemSelected(location.pathname, item.path),
            () => handleNavClick(item.path)
          )
        )}
        {visibleAdminItems.length > 0 && (
          <>
            <Divider
              sx={{
                my: 1,
                mx: 2,
                borderColor: '#68998C',
                borderBottomWidth: 2,
                opacity: 0.85,
              }}
            />
            {visibleAdminItems.map((item) =>
              renderMenuItem(
                item,
                isMenuItemSelected(location.pathname, item.path),
                () => handleNavClick(item.path)
              )
            )}
          </>
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#68998C', // Основной цвет для AppBar
          color: '#FFFFFF',
        }}
      >
        <Toolbar>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: '#FFFFFF'
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, color: '#FFFFFF' }}>
            Электронная база данных учета произведений искусства
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: '#FFFFFF' }}>{user?.full_name || user?.username}</Typography>
            <IconButton onClick={handleMenuClick} sx={{ color: '#FFFFFF' }}>
              <AccountCircle />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              sx={{
                '& .MuiPaper-root': {
                  backgroundColor: '#A7CBC2', // Светлый фон для меню
                },
              }}
            >
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  '&:hover': {
                    backgroundColor: '#99BFC7', // Светлый вторичный A при наведении
                  },
                }}
              >
                <ListItemIcon>
                  <Logout fontSize="small" sx={{ color: '#57726C' }} />
                </ListItemIcon>
                <ListItemText sx={{ color: '#57726C' }}>Выход</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#A7CBC2', // Светлый фон для drawer
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#A7CBC2', // Светлый фон для drawer
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}

